import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SUPPORTED_CURRENCIES, formatMinorAmount, type ReceivablesReportOutput, type InvoiceBrandingProfileOutput } from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { ClientSearchSelect } from './ClientSearchSelect.js';
import { DateInput } from './DateInput.js';
import { fmtDate } from '../lib/date.js';

const field = 'w-full bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent';
function money(value: number, currency: string) { return `${formatMinorAmount(value, true)} ${currency}`; }

export function FirmReceivablesPanel({ mode, onSelectClient }: { mode: 'balances' | 'transactions'; onSelectClient: (clientId: string) => void }) {
  const [clientId, setClientId] = useState('');
  const [currency, setCurrency] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [brandingProfileId, setBrandingProfileId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const profiles = useQuery({
    queryKey: ['invoice-branding-profiles'],
    queryFn: async () => {
      const res = await fetch('/v1/invoice-branding/profiles', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: InvoiceBrandingProfileOutput[] } };
      return body.data.items;
    },
  });
  const activeProfiles = (profiles.data ?? []).filter((profile) => profile.active);
  useEffect(() => {
    if (!brandingProfileId) {
      const fallback = activeProfiles.find((profile) => profile.isDefault) ?? activeProfiles[0];
      if (fallback) setBrandingProfileId(fallback.id);
    }
  }, [brandingProfileId, profiles.data]);
  const report = useQuery({
    queryKey: ['receivables-report', clientId, currency, from, to, transactionType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (clientId) params.set('clientId', clientId);
      if (currency) params.set('currency', currency);
      if (from) params.set('from', `${from}T00:00:00.000Z`);
      if (to) params.set('to', `${to}T00:00:00.000Z`);
      if (transactionType) params.set('transactionType', transactionType);
      const res = await fetch(`/v1/invoices/receivables/report?${params}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: ReceivablesReportOutput };
      return body.data;
    },
  });

  async function downloadPdf() {
    if (!report.data || !brandingProfileId) return;
    setDownloading(true); setPdfError(null);
    try {
      const params = new URLSearchParams({ brandingProfileId });
      if (clientId) params.set('clientId', clientId);
      if (currency) params.set('currency', currency);
      if (from) params.set('from', `${from}T00:00:00.000Z`);
      if (to) params.set('to', `${to}T00:00:00.000Z`);
      if (transactionType) params.set('transactionType', transactionType);
      const res = await fetch(`/v1/invoices/receivables/report/pdf?${params}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const url = URL.createObjectURL(await res.blob());
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = 'receivables-report.pdf'; anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : 'PDF generation failed');
    } finally { setDownloading(false); }
  }

  return (
    <div className="space-y-4">
      <div className="bg-bg-panel border border-border rounded-xl p-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2">
        <ClientSearchSelect value={clientId} onChange={setClientId} placeholder="All clients" className={field} wrapperClassName="w-full" />
        <select aria-label="Report currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className={field}>
          <option value="">All currencies</option>{SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <DateInput aria-label="Report from date" value={from} onChange={(e) => setFrom(e.target.value)} className={field} />
        <DateInput aria-label="Report to date" value={to} onChange={(e) => setTo(e.target.value)} className={field} />
        {mode === 'transactions' ? <select aria-label="Transaction type" value={transactionType} onChange={(e) => setTransactionType(e.target.value)} className={field}>
          <option value="">All transaction types</option><option value="ISSUED">Issued</option><option value="PAYMENT">Payments</option><option value="REVERSAL">Reversals</option>
        </select> : <div className="hidden xl:block" />}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <select aria-label="Full report branding" value={brandingProfileId} onChange={(e) => setBrandingProfileId(e.target.value)} className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white max-w-52">
          {activeProfiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
        </select>
        <button type="button" disabled={downloading || !brandingProfileId} onClick={() => void downloadPdf()} className="bg-accent text-accent-fg rounded-lg px-3 py-1.5 text-sm disabled:opacity-50">{downloading ? 'Generating PDF…' : 'Download full PDF report'}</button>
      </div>
      {pdfError && <p className="text-sm text-rose-300">{pdfError}</p>}

      {report.isLoading && <div className="bg-bg-panel border border-border rounded-xl p-10 text-center text-sm text-muted">Building the receivables report…</div>}
      {report.error && <div className="bg-bg-panel border border-rose-800 rounded-xl p-4 text-sm text-rose-300">{(report.error as Error).message}</div>}

      {report.data && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-3">
            {report.data.summary.map((summary) => (
              <div key={summary.currency} className="bg-bg-panel border border-border rounded-xl p-4 grid grid-cols-2 gap-x-3 gap-y-2">
                <div className="col-span-2 text-sm font-semibold text-white">{summary.currency} receivables</div>
                <Metric label="Invoiced" value={money(summary.invoiced, summary.currency)} />
                <Metric label="Received" value={money(summary.received, summary.currency)} />
                <Metric label="Outstanding" value={money(summary.outstanding, summary.currency)} />
                <Metric label="Overdue" value={money(summary.overdue, summary.currency)} tone="rose" />
                <Metric label="Current" value={money(summary.current, summary.currency)} />
                <Metric label="1–30 days" value={money(summary.aging1_30, summary.currency)} />
                <Metric label="31–60 days" value={money(summary.aging31_60, summary.currency)} />
                <Metric label="61–90 days" value={money(summary.aging61_90, summary.currency)} />
                <Metric label="90+ days" value={money(summary.aging90Plus, summary.currency)} />
              </div>
            ))}
          </div>

          {mode === 'balances' ? (
            <div className="bg-bg-panel border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[1250px]">
                <thead><tr className="bg-bg-card border-b border-border text-left text-xs text-muted">
                  <th className="px-4 py-3 font-medium">Client</th><th className="px-4 py-3 font-medium">Open</th><th className="px-4 py-3 font-medium text-right">Invoiced</th><th className="px-4 py-3 font-medium text-right">Received</th><th className="px-4 py-3 font-medium text-right">Current</th><th className="px-4 py-3 font-medium text-right">1–30</th><th className="px-4 py-3 font-medium text-right">31–60</th><th className="px-4 py-3 font-medium text-right">61–90</th><th className="px-4 py-3 font-medium text-right">90+</th><th className="px-4 py-3 font-medium text-right">Outstanding</th><th className="px-4 py-3 font-medium">Oldest due</th><th className="px-4 py-3 font-medium text-right">Statement</th>
                </tr></thead>
                <tbody className="divide-y divide-border/50">
                  {report.data.clients.length === 0 && <tr><td colSpan={12} className="px-4 py-10 text-center text-muted">No outstanding client balances match these filters.</td></tr>}
                  {report.data.clients.map((client) => (
                    <tr key={`${client.clientCode}-${client.currency}`} className="hover:bg-bg-hover/30">
                      <td className="px-4 py-3"><div className="text-white">{client.clientCompany ?? client.clientName}</div><div className="text-xs text-muted">{client.clientName} · {client.clientCode} · {client.currency}</div></td>
                      <td className="px-4 py-3 text-muted">{client.openInvoices}</td>
                      <td className="px-4 py-3 text-right text-white">{money(client.invoiced, client.currency)}</td>
                      <td className="px-4 py-3 text-right text-emerald-300">{money(client.received, client.currency)}</td>
                      <td className="px-4 py-3 text-right text-muted">{money(client.current, client.currency)}</td>
                      <td className="px-4 py-3 text-right text-muted">{money(client.aging1_30, client.currency)}</td>
                      <td className="px-4 py-3 text-right text-muted">{money(client.aging31_60, client.currency)}</td>
                      <td className="px-4 py-3 text-right text-muted">{money(client.aging61_90, client.currency)}</td>
                      <td className="px-4 py-3 text-right text-rose-300">{money(client.aging90Plus, client.currency)}</td>
                      <td className="px-4 py-3 text-right text-white font-medium">{money(client.outstanding, client.currency)}</td>
                      <td className="px-4 py-3 text-muted">{client.oldestDueDate ? fmtDate(client.oldestDueDate) : '—'}</td>
                      <td className="px-4 py-3 text-right">{client.clientId ? <button type="button" onClick={() => onSelectClient(client.clientId!)} className="text-accent hover:underline">Open</button> : <span className="text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-bg-panel border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm min-w-[1200px]">
                <thead><tr className="bg-bg-card border-b border-border text-left text-xs text-muted">
                  <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Client</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium">Reference</th><th className="px-4 py-3 font-medium">Details</th><th className="px-4 py-3 font-medium text-right">Debit</th><th className="px-4 py-3 font-medium text-right">Credit</th><th className="px-4 py-3 font-medium text-right">Balance</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Due</th>
                </tr></thead>
                <tbody className="divide-y divide-border/50">
                  {report.data.transactions.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted">No transactions match these filters.</td></tr>}
                  {report.data.transactions.map((entry) => (
                    <tr key={entry.id} className="hover:bg-bg-hover/30">
                      <td className="px-4 py-3 text-muted">{fmtDate(entry.date)}</td>
                      <td className="px-4 py-3"><div className="max-w-48 truncate text-white" title={entry.clientName}>{entry.clientName}</div><div className="text-xs text-muted">{entry.clientCode}</div></td>
                      <td className={`px-4 py-3 text-xs ${entry.type === 'PAYMENT' ? 'text-emerald-300' : entry.type === 'REVERSAL' ? 'text-amber-300' : 'text-white'}`}>{entry.type}</td>
                      <td className="px-4 py-3"><Link to={`/invoices/${entry.invoiceId}`} className="text-accent hover:underline">{entry.reference}</Link></td>
                      <td className="px-4 py-3 text-muted max-w-64 truncate" title={entry.details ?? undefined}>{entry.details ?? '—'}</td>
                      <td className="px-4 py-3 text-right text-white">{entry.debit ? money(entry.debit, entry.currency) : '—'}</td>
                      <td className="px-4 py-3 text-right text-emerald-300">{entry.credit ? money(entry.credit, entry.currency) : '—'}</td>
                      <td className="px-4 py-3 text-right text-white">{money(entry.balance, entry.currency)}</td>
                      <td className="px-4 py-3 text-xs text-muted">{entry.status.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-muted">{fmtDate(entry.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'rose' }) {
  return <div><div className="text-[10px] text-muted">{label}</div><div className={`text-sm font-medium ${tone === 'rose' ? 'text-rose-300' : 'text-white'}`}>{value}</div></div>;
}
